import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DeviceType } from '@core/device-detection/device.type';
import { DeviceDetectionService } from '@core/device-detection/device-detection.service';
import { AppConfigService } from '@core/index';
import { LanguageService } from '@core/language/language.service';
import { SessionStorageService } from '@core/storage/session-storage.service';
import { TextContentService } from '@core/text-content/text-content.service';
import { UIModule } from '@shared/ui';
import { BehaviorSubject, Observable, share } from 'rxjs';

interface SmartBannerContent {
  appStoreDownload: string;
  googlePlayDownload: string;
  smartBannerTitle: string;
  smartBannerDescription: string;
  smartBannerInstall: string;
}

@Component({
  selector: 'jb-smart-banner',
  standalone: true,
  templateUrl: './smart-banner.component.html',
  styleUrls: ['./smart-banner.component.scss'],
  imports: [CommonModule, UIModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartBannerComponent implements OnInit, OnDestroy {
  private sessionStorage = inject(SessionStorageService);
  private readonly DISMISSED_KEY = 'jb-smart-banner-dismissed';
  private readonly DISMISSAL_DURATION_DAYS = 90;

  isVisible$ = new BehaviorSubject<boolean>(false);
  deviceType: DeviceType;
  appStoreUrl: string;
  appStoreBadge: string;
  appName: string;
  appDescription: string;
  currentLanguage: string;

  downloadContent$: Observable<SmartBannerContent> = this.textContentService
    .getJsonAsset('app-store-text')
    .pipe(share());

  constructor(
    private deviceService: DeviceDetectionService,
    private textContentService: TextContentService,
    private appConfigService: AppConfigService,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.currentLanguage = this.languageService.getLanguage();
    this.deviceType = this.deviceService.detectDeviceType();

    // Only show on mobile devices (Android) and when feature flag is enabled
    if (
      this.deviceType === DeviceType.Android &&
      this.appConfigService.showAndroidAppBanner
    ) {
      // Check if banner was previously dismissed
      const isDismissed = this.isBannerDismissed();

      if (!isDismissed) {
        this.setupBannerContent();
        this.isVisible$.next(true);
      }
    }
  }

  private isBannerDismissed(): boolean {
    try {
      const dismissedData = localStorage.getItem(this.DISMISSED_KEY);
      if (!dismissedData) {
        return false;
      }

      const { dismissedAt } = JSON.parse(dismissedData);
      const dismissalDate = new Date(dismissedAt);
      const now = new Date();

      // Calculate days since dismissal
      const daysSinceDismissal =
        (now.getTime() - dismissalDate.getTime()) / (1000 * 60 * 60 * 24);

      // Return true if still within dismissal period
      return daysSinceDismissal < this.DISMISSAL_DURATION_DAYS;
    } catch (error) {
      console.warn('Error checking banner dismissal status:', error);
      return false;
    }
  }

  ngOnDestroy(): void {
    this.isVisible$.complete();
  }

  private setupBannerContent(): void {
    this.appName = 'JetBlue';
    this.appDescription = 'Book & manage trips';
    if (this.deviceType === DeviceType.Android) {
      this.appStoreUrl =
        this.appConfigService.mobileAppStoreBadges.android.storeUrl;
      this.appStoreBadge =
        this.appConfigService.mobileAppStoreBadges.android[
          this.currentLanguage
        ];
    }
  }

  dismissBanner(): void {
    const dismissalData = {
      dismissedAt: new Date().toISOString(),
      dismissedForDays: this.DISMISSAL_DURATION_DAYS,
    };

    localStorage.setItem(this.DISMISSED_KEY, JSON.stringify(dismissalData));
    this.isVisible$.next(false);
  }

  /**
   * Get the number of days remaining before the banner will show again
   * Returns 0 if banner is not dismissed or dismissal period has expired
   */
  getDaysUntilBannerShows(): number {
    try {
      const dismissedData = localStorage.getItem(this.DISMISSED_KEY);
      if (!dismissedData) {
        return 0;
      }

      const { dismissedAt } = JSON.parse(dismissedData);
      const dismissalDate = new Date(dismissedAt);
      const now = new Date();

      const daysSinceDismissal =
        (now.getTime() - dismissalDate.getTime()) / (1000 * 60 * 60 * 24);
      const remainingDays = this.DISMISSAL_DURATION_DAYS - daysSinceDismissal;

      return Math.max(0, Math.ceil(remainingDays));
    } catch (error) {
      console.warn('Error calculating days until banner shows:', error);
      return 0;
    }
  }
  openApp() {
    if (this.deviceType === DeviceType.Android) {
      window.location.href =
        'intent://#Intent;package=com.jetblue.JetBlueAndroid;scheme=jetblue;end';
    }
  }
}
